import React from "react";
import {
  Box,
  Flex,
  Text,
  HStack,
  VStack,
  Icon,
  Image as ChakraImage,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FaCartShopping } from "react-icons/fa6";
import { Trans } from "@lingui/macro";

import Button from "../../components/Button";
import { formatMoney } from "../../utils";

const money = (v, currency) => formatMoney(v, { formatWithSymbol: true, currency: currency ?? "IDR" });

// Price (selling) + strikethrough base + discount badge.
const PriceBlock = ({ product, currency, sizes }) => (
  <VStack spacing="4px" align="flex-start" w="100%">
    <Text color="black" fontSize={sizes.price} fontFamily="IBM Plex Mono" fontWeight="700" lineHeight={sizes.priceLh}>
      {money(product.selling_price, currency)}
    </Text>
    {product.discount_percentage ? (
      <HStack spacing="8px" align="center" h="18px">
        <Text color="#929393" fontSize="12px" fontFamily="IBM Plex Mono" textDecoration="line-through" lineHeight="18px">
          {money(product.base_price, currency)}
        </Text>
        <Flex bg="#FFDCDC" borderRadius="8px" px="6px" py="3px" align="center" justify="center">
          <Text color="#E53E3E" fontSize="11px" fontFamily="IBM Plex Mono" fontWeight="700" lineHeight="11px">
            -{product.discount_percentage}%
          </Text>
        </Flex>
      </HStack>
    ) : null}
  </VStack>
);

const SoldRow = ({ product, fontSize }) => (
  <HStack spacing="8px" align="center">
    <Icon as={FaCartShopping} boxSize={"16px"} color="#929393" />
    <Text color="black" fontSize={fontSize} fontFamily="IBM Plex Mono" fontWeight="400" lineHeight="20px">
      {product.sold_stock > 1000000 ? "1.000.000+" : product.sold_stock.toLocaleString("id-ID")} <Trans>Terjual</Trans>
    </Text>
  </HStack>
);

const BuyButton = ({ onClick, h, fontSize }) => (
  <Button
    label={<Trans>Beli Sekarang</Trans>}
    backgroundColor="saweria-yellow.normal"
    marginBottom={0}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    w="100%"
    h={h}
    fontSize={fontSize}
    textAlign="center"
  />
);

const TokoProductCardMobile = ({ product, currency, onBuy }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onBuy}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onBuy()}
    cursor="pointer"
    _hover={{ transform: "translateY(-2px)", transition: "0.2s" }}
    w="100%"
    px="8px"
    py="16px"
    bg="#F2F7F5"
    borderRadius="6px"
    border="1px solid #BEC1C1"
  >
    <Flex gap="8px" flex="1 1 0">
      <Box w="80px" h="80px" borderRadius="6px" overflow="hidden" flexShrink={0} bg="#E2E8E6">
        {product.cover && (
          <ChakraImage src={product.cover} alt={product.name} w="80px" h="80px" objectFit="cover" />
        )}
      </Box>
      <Flex flex="1 1 0" direction="column" gap="8px" minW={0}>
        <Text color="black" fontSize="14px" fontFamily="IBM Plex Mono" fontWeight="700" lineHeight="20px" noOfLines={2}>
          {product.name}
        </Text>
        <PriceBlock product={product} currency={currency} sizes={{ price: "14px", priceLh: "20px" }} />
        <SoldRow product={product} fontSize="10px" />
        <BuyButton onClick={onBuy} h="36px" fontSize="14px" />
      </Flex>
    </Flex>
  </Box>
);

const TokoProductCard = ({ product, currency, onBuy }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  if (isMobile) {
    return <TokoProductCardMobile product={product} currency={currency} onBuy={onBuy} />;
  }
  return (
    <Flex
      role="button"
      tabIndex={0}
      onClick={onBuy}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onBuy()}
      cursor="pointer"
      _hover={{ transform: "translateY(-2px)", transition: "0.2s" }}
      w="100%"
      h="100%"
      direction="column"
      justify="space-between"
      p="16px"
      bg="#F2F7F5"
      borderRadius="6px"
      border="1px solid #BEC1C1"
      overflow="hidden"
      gap="16px"
    >
      <Flex direction="column" gap="16px" w="100%">
        <Box w="100%" h="243px" borderRadius="6px" overflow="hidden" bg="#E2E8E6">
          {product.cover && (
            <ChakraImage src={product.cover} alt={product.name} w="100%" h="243px" objectFit="cover" />
          )}
        </Box>
        <VStack spacing="12px" align="flex-start" w="100%">
          <Text color="black" fontSize="16px" fontFamily="IBM Plex Mono" fontWeight="700" lineHeight="24px" noOfLines={2} w="100%">
            {product.name}
          </Text>
          <PriceBlock product={product} currency={currency} sizes={{ price: "18px", priceLh: "28px" }} />
          <SoldRow product={product} fontSize="14px" />
        </VStack>
      </Flex>
      <BuyButton onClick={onBuy} h="46px" fontSize="16px" />
    </Flex>
  );
};

export { TokoProductCard };
